import { STATUS_LABELS, type Status } from '@helpdesk/shared';
import { CircleCheckBig, FileText, ImageIcon, Lock } from 'lucide-react';
import { formatBytes, formatDateTime, timeAgo } from '../lib/format.ts';
import type { AttachmentView, TicketDetail, TimelineEntry, UserRef } from '../lib/types.ts';
import { Avatar } from './ui/misc.tsx';

export function AttachmentList({ files }: { files: AttachmentView[] }) {
  if (!files.length) return null;
  return (
    <ul className="mt-3 flex flex-wrap gap-2">
      {files.map((f) => (
        <li key={f.id}>
          <a
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="flex max-w-64 items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm hover:border-brand"
          >
            {f.mimeType === 'application/pdf' ? (
              <FileText className="size-4 shrink-0 text-muted" aria-hidden />
            ) : (
              <ImageIcon className="size-4 shrink-0 text-muted" aria-hidden />
            )}
            <span className="truncate">{f.name}</span>
            <span className="shrink-0 text-xs text-muted">{formatBytes(f.sizeBytes)}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function When({ iso }: { iso: string }) {
  return (
    <time dateTime={iso} title={formatDateTime(iso)} className="text-xs text-muted">
      {timeAgo(iso)}
    </time>
  );
}

function Message({
  author,
  isIt,
  body,
  createdAt,
  files,
  note = false,
}: {
  author: UserRef | null;
  isIt: boolean;
  body: string;
  createdAt: string;
  files: AttachmentView[];
  note?: boolean;
}) {
  // No author: a reply that came into the helpdesk mailbox from someone else.
  const name = author?.name ?? (note ? 'By email' : 'Someone');
  return (
    <li className="flex gap-3">
      <Avatar name={name} className={isIt ? '' : 'bg-surface-2 text-muted'} />
      <div
        className={`min-w-0 flex-1 rounded-xl border px-4 py-3 ${
          note
            ? 'border-warning/40 bg-warning-soft'
            : isIt
              ? 'border-brand/30 bg-brand-soft/40'
              : 'border-line bg-surface'
        }`}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-semibold">{name}</span>
          {isIt && !note && (
            <span className="rounded bg-brand px-1.5 text-[10px] font-bold tracking-wide text-on-brand uppercase">
              IT
            </span>
          )}
          {note && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-warning">
              <Lock className="size-3" aria-hidden />
              Internal note — only IT can see this
            </span>
          )}
          <When iso={createdAt} />
        </div>
        <p className="mt-1.5 break-words whitespace-pre-wrap">{body}</p>
        <AttachmentList files={files} />
      </div>
    </li>
  );
}

function systemText(e: TimelineEntry, forStaff: boolean): string {
  const who = e.author?.name ?? 'The helpdesk';
  const data = e.data ?? {};
  switch (e.kind) {
    case 'status': {
      const to = data.to as Status;
      if (data.auto) return `Closed automatically after being resolved for a week`;
      if (to === 'void')
        return forStaff
          ? 'IT cancelled this ticket'
          : `${who} voided this ticket${e.body ? `: ${e.body}` : ''}`;
      if (data.restored) return `${who} restored this ticket`;
      const label = forStaff && to === 'waiting' ? 'waiting for your reply' : STATUS_LABELS[to].toLowerCase();
      if (data.from === 'resolved' && to === 'in_progress') return `${who} reopened the ticket`;
      return `${who} marked this ${label}`;
    }
    case 'priority':
      return `${who} changed the priority from ${String(data.from)} to ${String(data.to)}`;
    case 'assignment':
      if (data.reason === 'cover')
        return `Assigned to ${String(data.toName)}, who is covering for IT staff on leave`;
      return data.toName ? `${who} assigned this to ${String(data.toName)}` : `${who} unassigned this ticket`;
    default:
      return '';
  }
}

export function TicketTimeline({ detail, forStaff }: { detail: TicketDetail; forStaff: boolean }) {
  const { ticket, requester } = detail;
  return (
    <ol className="space-y-4">
      <Message
        author={requester}
        isIt={false}
        body={ticket.description}
        createdAt={ticket.createdAt}
        files={detail.attachments}
      />
      {detail.timeline.map((e) => {
        if (e.kind === 'reply' || e.kind === 'note') {
          return (
            <Message
              key={e.id}
              author={e.author}
              isIt={!!e.author && e.author.role !== 'staff' && e.author.id !== requester.id}
              body={e.body ?? ''}
              createdAt={e.createdAt}
              files={e.attachments}
              note={e.kind === 'note'}
            />
          );
        }
        if (e.kind === 'status' && (e.data?.to as Status) === 'resolved') {
          return (
            <li key={e.id} className="rounded-xl border border-brand/40 bg-brand-soft p-4">
              <p className="flex items-center gap-2 font-semibold text-brand-ink">
                <CircleCheckBig className="size-5" aria-hidden />
                Resolved by {e.author?.name ?? 'IT'}
                <When iso={e.createdAt} />
              </p>
              {e.body && <p className="mt-2 whitespace-pre-wrap">{e.body}</p>}
            </li>
          );
        }
        return (
          <li key={e.id} className="flex items-center gap-3 pl-11 text-sm text-muted">
            <span className="h-px w-4 bg-line" aria-hidden />
            <span>
              {systemText(e, forStaff)} · <When iso={e.createdAt} />
            </span>
          </li>
        );
      })}
    </ol>
  );
}
